import {
	Button,
	Carousel,
	CarouselContent,
	CarouselItem,
	CarouselNext,
	CarouselPrevious,
	CarouselProgressIndicator,
	CarouselThumbnails,
	cn,
	ImageBadge,
} from "@babybjorn/ui";
import { useMemo, useState, type FC } from "react";
import { Dialog, DialogContent } from "../Dialog/Dialog";
import Hotspots from "../general/Hotspots";
import type { HotspotProps } from "../general/Hotspots/components/Hotspot/Hotspot";
import ImageContainer, {
	type VimeoResponse,
} from "../general/ImageContainers/ImageContainer";
import { useIsTablet } from "../../hooks/use-is-screensize";
import Icon from "../general/Icon";

export type ProductImage = {
	alt: string;
	imgSrc: string;
	videoSrc?: string;
	youtubeEmbedId?: string;
	vimeoResponse?: VimeoResponse;
	isProductImage?: boolean;
	badge?: string;
	addPadding?: boolean;
	hotspots?: HotspotProps[];
	uid?: string;
};

type ProductGalleryProps = {
	productImage?: ProductImage;
	lookImage?: ProductImage;
	restImages: (ProductImage | ProductImage[])[];
	badge?: string;
};

const ProductGallery: FC<ProductGalleryProps> = ({
	productImage,
	lookImage,
	restImages,
	badge,
}) => {
	const isTablet = useIsTablet();
	const [showGallery, setShowGallery] = useState(false);

	const imagesToDisplay = useMemo(
		() =>
			(lookImage ? [lookImage, productImage, ...restImages] : restImages)
				.flat()
				.filter((_, i) => !isTablet || i !== 1),
		[lookImage, productImage, restImages, isTablet],
	);

	return (
		<div
			className={cn(
				"col-span-full md:col-span-6",
				"overflow-hidden max-md:-mx-[calc(var(--spacing-bb)+1%)]",
			)}
		>
			<section className={cn("isolate w-full")}>
				{productImage && (
					<Hotspots
						name="hotspots"
						hotspots={productImage.hotspots}
						productGallery
						className="max-md:hidden"
					>
						<ImageContainer
							imgSrc={productImage.imgSrc}
							alt={productImage.alt}
							ratio="square"
							priority
							className="relative"
						>
							{badge && <ImageBadge borderStyle="soft">{badge}</ImageBadge>}
						</ImageContainer>
					</Hotspots>
				)}

				<Carousel
					options={{
						loop: true,
						containScroll: "trimSnaps",
					}}
				>
					<CarouselContent className="items-center">
						{imagesToDisplay.map((image, index) => (
							<CarouselItem
								key={image?.uid ?? `${image?.imgSrc}-${index}`}
								className={cn(
									"overflow-hidden md:rounded-xs",
									"max-md:max-w-[calc(100vw-var(--spacing-bb))] max-md:p-1",
									"transform-gpu",
								)}
							>
								<Hotspots
									name="hotspots"
									hotspots={image?.hotspots}
									productGallery
								>
									<ImageContainer
										alt={image?.alt}
										hidePlaceholder={image?.addPadding}
										imgSrc={image?.imgSrc}
										objectFit={image?.addPadding ? "contain" : "cover"}
										addPadding={image?.addPadding}
										paddingVariant="gallery"
										hideZoomButton
										priority={index < 2}
										onClick={() => setShowGallery(true)}
										className="hover:cursor-zoom-in"
										aspectRatio={7 / 5}
										sizes="(max-width: 768px) 100vw, 50vw"
										videoSrc={image?.videoSrc}
										youtubeEmbedId={image?.youtubeEmbedId}
										vimeoResponse={image?.vimeoResponse}
									>
										{badge && (
											<ImageBadge
												borderStyle="hard"
												className={cn("md:hidden", {
													hidden: index !== 0,
												})}
											>
												{badge}
											</ImageBadge>
										)}
									</ImageContainer>
								</Hotspots>
							</CarouselItem>
						))}
					</CarouselContent>

					<CarouselPrevious
						variant="special"
						className={cn("top-[41.85%] translate-x-0.5 max-md:hidden")}
					/>

					<CarouselNext
						variant="special"
						className={cn("top-[41.85%] -translate-x-0.5 max-md:hidden")}
					/>

					<CarouselThumbnails
						className="right-0 bottom-5 left-0 max-md:absolute"
						thumbnails={imagesToDisplay.map((image, index) => (
							<ImageContainer
								key={image?.uid ?? `${image?.imgSrc}-${index}`}
								alt={image?.alt}
								imgSrc={image?.imgSrc}
								objectFit="cover"
								hideZoomButton
								hidePlaceholder
								aspectRatio={7 / 5}
								sizes="(max-width: 768px) 100vw, 50vw"
							>
								{image?.vimeoResponse && (
									<Button
										className={cn(
											"absolute left-1/2 -translate-x-1/2 -translate-y-1/2",
											"top-[48%] h-12 w-12",
										)}
										variant="special"
									>
										<Icon icon="play" width={20} height={20} />
									</Button>
								)}
							</ImageContainer>
						))}
					/>

					<div className="pointer-events-none absolute inset-0 hidden aspect-[5/7] md:inline">
						<CarouselProgressIndicator className="bottom-bb right-bb text-gray-4 absolute rounded-full border-0 bg-white/70" />
					</div>
				</Carousel>
			</section>

			<Dialog open={showGallery} onOpenChange={(open) => setShowGallery(open)}>
				<DialogContent
					className={cn(
						"h-[calc(100vh-var(--spacing-bb))] w-[calc(100vw-var(--spacing-bb))] max-w-[unset]",
					)}
					aria-describedby="product-gallery-expanded-view"
				>
					<div className="cursor-pointer overflow-auto">
						{imagesToDisplay.map((image, index) => (
							<ImageContainer
								key={image?.uid ?? `${image?.imgSrc}-${index}`}
								alt={image?.alt}
								hidePlaceholder={image?.addPadding}
								imgSrc={image?.imgSrc}
								objectFit={image?.addPadding ? "contain" : "cover"}
								addPadding={image?.addPadding}
								paddingVariant="gallery"
								onClick={() => setShowGallery(false)}
								hideZoomButton
							/>
						))}
					</div>
				</DialogContent>
			</Dialog>
		</div>
	);
};

export default ProductGallery;
